import { LocalDate } from '@js-joda/core';
import { SpoolStatus } from 'providers/api';
import * as PCConstants from '../PCConstants';
import { headMetresToKpa } from '../hydraulics';
import PCNode from '../Nodes/PCNode';
import PCPipe from '../Pipes/PCPipe';
import pairKey from './pairKey';

const radiansToDegrees = (radians: number) => {
  const pi = Math.PI;
  return radians * (180 / pi);
};

// Everything on a spool that is history or annotation rather than geometry. An options object rather
// than more positional arguments, so a wholesale rebuild reads as data and a missing field is visibly
// undefined instead of silently shifting the argument list.
export interface PCSpoolOptions {
  spoolColour?: string;
  totalVolumeThroughSpool?: number;
  totalDryTonnesThroughSpool?: number;
  isInspectionPoint?: boolean;
  inspectionPointName?: string;
  name?: string;
  installedOn?: LocalDate;
  removedOn?: LocalDate;
  status?: SpoolStatus;
  other?: string;
  level?: string;
  area?: string;
}

export default class PCSpool {
  spoolId: string;

  chokeLength: number;

  pointA: PCNode;

  pointB: PCNode;

  pipe: PCPipe;

  boreHoleAngle: number;

  allowFlowAB: boolean;

  allowFlowBA: boolean;

  spoolColour: string;

  isFlowingFromAtoB: boolean;

  showConstraintArrow: boolean;

  // Cumulative throughput accrued on pour completion. Wear is monitored against the tonnes.
  totalVolumeThroughSpool: number;

  totalDryTonnesThroughSpool: number;

  isInspectionPoint: boolean;

  inspectionPointName?: string;

  name?: string;

  installedOn?: LocalDate;

  removedOn?: LocalDate;

  // Inactive spools are drawn faintly and never routed over.
  status: SpoolStatus;

  other?: string;

  level?: string;

  area?: string;

  constructor(
    spoolId: string,
    chokeLength: number,
    pointA: PCNode,
    pointB: PCNode,
    pipe: PCPipe,
    allowFlowAB: boolean,
    allowFlowBA: boolean,
    options: PCSpoolOptions | string = {},
  ) {
    // The eighth argument used to be the colour alone; keep accepting a bare string for that call shape.
    const resolved: PCSpoolOptions = typeof options === 'string' ? { spoolColour: options } : options;

    this.spoolId = spoolId;
    this.chokeLength = chokeLength;
    this.pointA = pointA;
    this.pointB = pointB;
    this.pipe = pipe;
    this.allowFlowAB = allowFlowAB;
    this.allowFlowBA = allowFlowBA;
    this.spoolColour = resolved.spoolColour || PCConstants.SPOOL_COLOUR;
    const verticalDistance = this.pointB.coords.z - this.pointA.coords.z;
    const horizontalDistance = Math.sqrt(((this.pointB.coords.x - this.pointA.coords.x) ** 2) + ((this.pointB.coords.y - this.pointA.coords.y) ** 2));
    this.boreHoleAngle = Math.abs(radiansToDegrees(Math.atan(verticalDistance / horizontalDistance)));
    this.isFlowingFromAtoB = allowFlowAB;
    this.showConstraintArrow = false;
    this.totalVolumeThroughSpool = resolved.totalVolumeThroughSpool ?? 0;
    this.totalDryTonnesThroughSpool = resolved.totalDryTonnesThroughSpool ?? 0;
    this.isInspectionPoint = !!resolved.isInspectionPoint;
    this.inspectionPointName = resolved.inspectionPointName;
    this.name = resolved.name;
    this.installedOn = resolved.installedOn;
    this.removedOn = resolved.removedOn;
    this.status = resolved.status ?? SpoolStatus.Active;
    this.other = resolved.other;
    this.level = resolved.level;
    this.area = resolved.area;
  }

  // Canonical identity of the edge this spool occupies. Spool ids are opaque GUIDs, so lookups
  // by endpoint go through this key.
  pairKey(): string {
    return pairKey(this.pointA.id, this.pointB.id);
  }

  length(): number {
    if (this.pointA && this.pointB) {
      let len = 0;
      len += this.chokeLength || 0;
      len += this.pointA.coords.length3dTo(this.pointB.coords);
      return len;
    }
    return 0;
  }

  deltaZ(): number { // positive for A < B
    if (this.pointA && this.pointB) {
      return this.pointA.coords.z - this.pointB.coords.z;
    }
    return 0;
  }

  getFrictionLoss(): number { // kPa, negative for loss
    let frictionLoss = 0;
    if (this.pipe && this.pipe.friction) frictionLoss = -1 * this.length() * this.pipe.friction;
    // else console.log('no pipe or no friction', this);
    return frictionLoss;
  }

  // The head follows the route's direction of travel, not the stored flags - bi-directional boreholes route against them.
  getPressureDelta(wetDensity: number, flowForward?: boolean): number { // kPa, negative for loss
    if (!wetDensity) return 0;
    const head = flowForward ? -1 * this.deltaZ() : this.deltaZ(); // deltaZ = this.pointA.coords.z - this.pointB.coords.z;
    return headMetresToKpa(head, wetDensity) - this.getFrictionLoss(); // kPa
  }

  changeColour(colour: string) {
    this.spoolColour = colour;
  }

  changePipe(pipe: PCPipe) {
    this.pipe = pipe;
  }
}
